import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { readUtms, trackConversion } from "@/lib/track";
import { ArrowRight, CheckCircle, Circle, Copy, PaperPlaneTilt, Warning } from "@phosphor-icons/react";

/**
 * /quick-quote — public shareable one-page form.
 * Prospect picks a country + services + basic contact info + notes.
 * On submit: backend saves it, emails OBA admins + confirms to lead, returns reference.
 */
export default function QuickQuote() {
  const [countries, setCountries] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    full_name: "", email: "", phone: "",
    country_slug: params.get("country") || "",
    business_activity: params.get("business_type") || "",
    notes: "",
  });
  const [picked, setPicked] = useState({}); // { serviceSlug: true }
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  // Load countries once
  useEffect(() => {
    api.get("/countries").then((r) => setCountries(r.data.filter((c) => c.active !== false)));
  }, []);

  // Load services whenever country changes
  useEffect(() => {
    if (!form.country_slug) { setAllServices([]); return; }
    setServicesLoading(true);
    api.get(`/services?country=${form.country_slug}`)
      .then((r) => setAllServices(r.data))
      .finally(() => setServicesLoading(false));
  }, [form.country_slug]);

  const country = countries.find((c) => c.slug === form.country_slug);
  const currency = country?.currency_code || "USD";

  // Group services by category
  const grouped = useMemo(() => {
    const out = {};
    allServices.forEach((s) => {
      (out[s.category || "other"] = out[s.category || "other"] || []).push(s);
    });
    return out;
  }, [allServices]);

  // Estimated total in local currency + USD (rough)
  const totals = useMemo(() => {
    let local = 0;
    allServices.forEach((s) => {
      if (!picked[s.slug]) return;
      const p = s.price || {};
      local += Number(p.one_time || 0) + Number(p.monthly || 0);
    });
    const fx = Number(country?.fx_rate || 1); // local per USD
    const usd = fx > 0 ? local / fx : local;
    return { local, usd };
  }, [picked, allServices, country]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (slug) => setPicked((p) => ({ ...p, [slug]: !p[slug] }));

  async function submit() {
    setError("");
    if (!form.full_name || !form.email || !form.country_slug) {
      setError("Please provide your name, email and country of interest.");
      return;
    }
    setSubmitting(true);
    try {
      const selected = allServices.filter((s) => picked[s.slug]).map((s) => ({
        slug: s.slug,
        name: s.name,
        one_time_local: s.price?.one_time || 0,
        monthly_local: s.price?.monthly || 0,
        currency: currency,
        estimated_price_usd: (Number(s.price?.one_time || 0) + Number(s.price?.monthly || 0)) / Number(country?.fx_rate || 1),
      }));
      const payload = {
        ...form,
        country_name: country?.name || "",
        services: selected,
        estimated_total_local: totals.local,
        estimated_total_usd: totals.usd,
        currency,
        utm: readUtms() || {},
      };
      const r = await api.post("/quick-quote", payload);
      setResult(r.data);
      trackConversion("quick_quote", { value: Math.max(25, Math.round(totals.usd / 20)) });
    } catch (e) {
      setError(e?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // Success state
  if (result) return <SuccessCard result={result} onNewQuote={() => { setResult(null); setPicked({}); }} navigate={navigate} />;

  return (
    <div className="fade-in bg-gos-bone min-h-screen" data-testid="quick-quote-page">
      <section className="border-b border-gos-border bg-white">
        <div className="container-gos py-12 md:py-16">
          <div className="overline mb-3">One-page quotation</div>
          <h1 className="font-display text-3xl sm:text-5xl text-gos-charcoal tracking-tight leading-tight">
            Tell us what you need. <span className="italic text-gos-navy">We&apos;ll do the rest.</span>
          </h1>
          <p className="text-gos-slate mt-4 max-w-2xl">
            Pick your target country and the services you&apos;re considering. Submit
            and one of our advisors will reply with a personalised quotation within
            one business day.
          </p>
        </div>
      </section>

      <section className="container-gos py-10 md:py-14 grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
        {/* LEFT — form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact block */}
          <Card title="Your details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Full name *" value={form.full_name} onChange={(v) => setField("full_name", v)} testid="qq-name" />
              <Field label="Email *" type="email" value={form.email} onChange={(v) => setField("email", v)} testid="qq-email" />
              <Field label="Phone / WhatsApp" value={form.phone} onChange={(v) => setField("phone", v)} testid="qq-phone" />
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-gos-muted mb-1">Country of interest *</label>
                <select
                  data-testid="qq-country"
                  value={form.country_slug}
                  onChange={(e) => { setField("country_slug", e.target.value); setPicked({}); }}
                  className="w-full border border-gos-border rounded-sm px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-gos-navy"
                >
                  <option value="">Select a country…</option>
                  {countries.map((c) => (
                    <option key={c.slug} value={c.slug}>{c.flag} {c.name} ({c.currency_code})</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <Field label="Business activity" value={form.business_activity} onChange={(v) => setField("business_activity", v)}
                  placeholder="e.g. E-commerce, SaaS, consulting, food exports…" testid="qq-activity" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[10px] uppercase tracking-widest text-gos-muted mb-1">Notes (optional)</label>
                <textarea rows={3}
                  data-testid="qq-notes"
                  value={form.notes}
                  onChange={(e) => setField("notes", e.target.value)}
                  placeholder="Anything else that will help us prepare an accurate quote…"
                  className="w-full border border-gos-border rounded-sm px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-gos-navy"
                />
              </div>
            </div>
          </Card>

          {/* Services block */}
          <Card
            title={country ? `Services available in ${country.name}` : "Select a country to see services"}
            subtitle={country ? `Prices shown in ${currency}. Pick all that apply.` : ""}
          >
            {renderServicesBlock({
              hasCountry: Boolean(form.country_slug),
              loading: servicesLoading,
              services: allServices,
              grouped,
              picked,
              toggleService,
              currency,
            })}
          </Card>
        </div>

        {/* RIGHT — sticky summary */}
        <aside className="lg:sticky lg:top-24 space-y-4">
          <Card title="Estimated summary">
            <div className="space-y-1.5 text-sm">
              <Row label="Country" value={country?.name || "—"} />
              <Row label="Currency" value={currency} />
              <Row label="Services selected" value={String(Object.values(picked).filter(Boolean).length)} />
            </div>
            <div className="mt-5 pt-5 border-t border-gos-border">
              <div className="overline !text-gos-muted mb-1">Estimated total</div>
              <div className="font-display text-2xl text-gos-navy">
                {currency} {Number(totals.local).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </div>
              <div className="text-xs text-gos-muted mt-1">
                ≈ USD {Number(totals.usd).toLocaleString(undefined, { maximumFractionDigits: 0 })} · indicative one-time + first month
              </div>
            </div>
            {error && (
              <div className="mt-4 flex items-start gap-2 text-xs text-gos-maple bg-gos-maple/10 border border-gos-maple/40 rounded-sm p-3">
                <Warning size={16} weight="fill" className="shrink-0" /> {error}
              </div>
            )}
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              data-testid="qq-submit"
              className="btn-primary w-full justify-center mt-5 disabled:opacity-60"
            >
              {submitting ? "Submitting…" : (<><PaperPlaneTilt size={16} weight="bold" /> Submit for a quote</>)}
            </button>
            <p className="text-[11px] text-gos-muted mt-3 italic leading-relaxed">
              Submitting this form is a request for a personalised quotation. It is not a
              binding order and does not guarantee registration, banking approval, or any
              third-party approval.
            </p>
          </Card>
          <Card title="Share this form">
            <p className="text-xs text-gos-slate mb-3">Send this link to a colleague or client:</p>
            <ShareLink />
          </Card>
        </aside>
      </section>
    </div>
  );
}

/* ────────  Presentational helpers  ──────── */
function Card({ title, subtitle, children }) {
  return (
    <div className="bg-white border border-gos-border rounded-sm p-5 md:p-6">
      <div className="mb-4">
        <h2 className="font-display text-lg text-gos-navy leading-tight">{title}</h2>
        {subtitle && <p className="text-xs text-gos-muted mt-1">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", placeholder }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-gos-muted mb-1">{label}</label>
      <input
        type={type} value={value ?? ""} placeholder={placeholder} data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gos-border rounded-sm px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-gos-navy"
      />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-gos-muted">{label}</span>
      <span className="font-medium text-gos-charcoal">{value}</span>
    </div>
  );
}

function ShareLink() {
  const url = typeof window !== "undefined" ? `${window.location.origin}/quick-quote` : "";
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center gap-2">
      <input readOnly value={url} className="flex-1 border border-gos-border rounded-sm px-2 py-1.5 text-xs bg-gos-bone" data-testid="qq-share-url" />
      <button
        type="button"
        onClick={() => { navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
        className="btn-secondary text-xs px-3 py-1.5"
      >
        <Copy size={12} weight="bold" /> {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

function SuccessCard({ result, onNewQuote, navigate }) {
  return (
    <div className="fade-in bg-gos-bone min-h-screen flex items-center py-24" data-testid="quick-quote-success">
      <div className="container-gos max-w-xl mx-auto">
        <div className="bg-white border border-gos-border rounded-sm p-8 md:p-10 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-gos-maple/15 flex items-center justify-center">
            <CheckCircle size={40} weight="fill" className="text-gos-maple" />
          </div>
          <h1 className="font-display text-3xl text-gos-charcoal mt-6">Quote request received</h1>
          <p className="text-gos-slate mt-3 leading-relaxed">
            Thank you. Our team will reply with a personalised quotation within one business day.
          </p>
          <div className="mt-6 border-t border-b border-gos-border py-4">
            <div className="overline !text-gos-muted mb-1">Reference</div>
            <div className="font-mono text-lg text-gos-navy" data-testid="qq-reference">{result.reference}</div>
          </div>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <button onClick={onNewQuote} className="btn-secondary">Submit another <ArrowRight size={14} weight="bold" /></button>
            <button onClick={() => navigate("/")} className="btn-primary">Back to home</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const CATEGORY_LABELS = {
  formation: "Business Setup",
  presence: "Business Presence",
  growth: "Business Growth",
  expansion: "Investment & Expansion",
  referral: "Professional Referrals",
  office: "Office & Address",
};
function formatCategory(cat) {
  return CATEGORY_LABELS[cat] || cat.charAt(0).toUpperCase() + cat.slice(1);
}

function renderServicesBlock({ hasCountry, loading, services, grouped, picked, toggleService, currency }) {
  if (!hasCountry) {
    return <p className="text-sm text-gos-muted italic">Please select a country above first.</p>;
  }
  if (loading) {
    return <p className="text-sm text-gos-muted">Loading services…</p>;
  }
  if (services.length === 0) {
    return (
      <p className="text-sm text-gos-muted italic">
        No standard services listed for this market yet. Add notes above and we&apos;ll quote a custom plan.
      </p>
    );
  }
  return (
    <div className="space-y-6">
      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat}>
          <div className="overline text-gos-navy mb-3">{formatCategory(cat)}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {items.map((s) => {
              const on = Boolean(picked[s.slug]);
              return (
                <button
                  key={s.slug}
                  type="button"
                  onClick={() => toggleService(s.slug)}
                  data-testid={`qq-svc-${s.slug}`}
                  className={`text-left border rounded-sm p-4 transition-colors ${
                    on ? "bg-gos-navy text-white border-gos-navy" : "bg-white border-gos-border hover:border-gos-navy"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {on
                      ? <CheckCircle size={18} weight="fill" className="text-gos-maple mt-0.5 shrink-0" />
                      : <Circle size={18} weight="regular" className="text-gos-muted mt-0.5 shrink-0" />}
                    <div className="flex-1">
                      <div className="text-sm font-semibold">{s.name}</div>
                      <div className={`text-xs mt-1 font-mono ${on ? "text-white/80" : "text-gos-slate"}`}>
                        {s.price?.one_time ? `${currency} ${Number(s.price.one_time).toLocaleString()} one-time` : ""}
                        {s.price?.one_time && s.price?.monthly ? " · " : ""}
                        {s.price?.monthly ? `${currency} ${Number(s.price.monthly).toLocaleString()} /mo` : ""}
                        {!s.price?.one_time && !s.price?.monthly && "Referral / by-request"}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
